import { Button, Col, DatePicker, Form, Input, message, Modal, Row, Select, Upload } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react'
import { getallDeveloperForTask, getSingleResignation, updateResignation } from '../../utils/API';
import { UploadFile } from 'antd/lib';
import { DeleteOutlined, FileImageOutlined, FilePdfOutlined, FileUnknownOutlined, FileWordOutlined, UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/es/upload';
import { RcFile } from 'antd/lib/upload';
import { AuthContext } from '../../Auth/AuthContext';

interface EditResignationModalProps {
    visible: boolean;
    onCancel: () => void;
    onUpdate?: () => void;
    refreshData: () => void;
    resignationData?: any; // row data to edit
    currentResignationId: any;
    existingAuthData?: any; // pass logged in user data
}

const EditResignationModal: React.FC<EditResignationModalProps> = ({
    visible,
    onCancel,
    onUpdate,
    currentResignationId,
    existingAuthData,
    refreshData
}) => {
    const { authData } = useContext(AuthContext);
    const isAdmins = authData.roleId?.roleId === 1;
    const IMAGEURL = process.env.REACT_APP_IMAGE_URL + '/resignation/';
    const [form] = Form.useForm();
    const [users, setUsers] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [Document, setDocument] = useState<(string | File)[]>([]);
    const [fileListDocument, setFileListDocument] = useState<UploadFile<File>[]>([]);
    const [removeAttachments, setRemoveAttachments] = useState<string[]>([]);




    const handleDeleteDocument = (doc: string | File, index: number) => {
        // Remove from Document state
        const newDocument = [...Document];
        newDocument.splice(index, 1);
        setDocument(newDocument);

        // Remove from Upload file list state
        const newFileListDocument = [...fileListDocument];
        newFileListDocument.splice(index, 1);
        setFileListDocument(newFileListDocument);

        // If it's an existing file (string), add to removeAttachments
        if (typeof doc === "string") {
            setRemoveAttachments(prev => [...prev, doc]);
        }
    };

    const getFileIcon = (fileName: string) => {
        const ext = fileName.split(".").pop()?.toLowerCase();

        if (ext === "pdf") return <FilePdfOutlined className="text-2xl text-red-500 mb-1" />;

        if (ext === "doc" || ext === "docx") return <FileWordOutlined
            className="text-2xl text-blue-500 mb-1" />;
        if (ext === "jpg" || ext === "jpeg" || ext === "png")
            return <FileImageOutlined className="text-2xl text-green-500 mb-1" />;

        // default fallback
        return <FileUnknownOutlined className="text-2xl text-gray-500 mb-1" />;
    };



    const isAdmin = existingAuthData?.user?.roleId?.name === "Admin";

    // fetch developers for dropdown
    const getAllUsersData = async () => {
        try {
            const response = await getallDeveloperForTask("", "");
            setUsers(response?.data || []);
        } catch (error) {
            console.error("Error getting all users:", error);
        }
    };
    useEffect(() => {
        if (visible && currentResignationId?._id) {
            // Clear previous files
            setDocument([]);
            setFileListDocument([]);
            getSingleResignationID();
        }
    }, [visible, currentResignationId]);


    const getSingleResignationID = async () => {
        try {
            const res = await getSingleResignation(currentResignationId.developerId);
            const resignation = res?.data?.resignation;

            form.setFieldsValue({
                developerId: resignation?.developerId || null,
                date: resignation?.date ? dayjs(resignation.date) : null,
                approvedDate: resignation?.approvedDate
                    ? dayjs(resignation.approvedDate, "DD-MM-YYYY")
                    : "",
                description: resignation?.description || "",
            });

            if (resignation?.attachments && resignation.attachments.length > 0) {
                setDocument(resignation.attachments);

                const fileList = resignation.attachments.map((file: string, index: number) => ({
                    uid: `existing-${index}-${Date.now()}`,
                    name: file.split('/').pop() || 'attachment',
                    status: 'done',
                    url: `${IMAGEURL}${file}`,
                    ...(file.match(/\.(jpg|jpeg|png|gif|webp)$/i)
                        ? { preview: `${IMAGEURL}${file}` }
                        : {}),
                }));

                setFileListDocument(fileList);
            }
        } catch (error) {
            console.error("Error fetching resignation:", error);
        }
    };



    useEffect(() => {
        if (visible) {
            getAllUsersData();
        } else {
            form.resetFields();
        }
    }, [visible, currentResignationId]);
    const handleSubmit = async (values: any) => {
        try {
            setLoading(true);
            const formData = new FormData();

            // --- Append regular fields ---
            if (values.developerId) formData.append("developerId", values.developerId);
            formData.append("description", values.description || "");
            formData.append("date", values.date ? dayjs(values.date).toISOString() : "");
            formData.append(
                "approvedDate",
                values.approvedDate ? dayjs(values.approvedDate).format("DD-MM-YYYY") : ""
            );
            if (values.status) formData.append("status", values.status);

            const existingFiles = Array.isArray(Document) ? Document : [Document];

            existingFiles.forEach((file) => {
                if (file instanceof File) {
                    formData.append("attachments", file);
                } else {
                    formData.append("existingDocuments", file);
                }
            });

            removeAttachments.forEach((file) => {
                formData.append("removeAttachments", file);
            });

            // --- Optional: Add new summary ---
            if (values.newSummary) {
                formData.append("newSummary", JSON.stringify(values.newSummary));
            }

            // --- API Call ---
            await updateResignation(currentResignationId._id, formData);

            message.success("Resignation updated successfully!");
            refreshData();
            onUpdate?.();
            onCancel();
        } catch (error) {
            console.error("Error updating resignation:", error);
            message.error("Failed to update resignation");
        } finally {
            setLoading(false);
        }
    };





    return (
        <Modal
            open={visible}
            onCancel={onCancel}
            footer={null}
            width={600}
            centered={false}
            className="custom-modal"
            style={{
                bottom: 20,
                top: 20,
                maxHeight: 'calc(100vh - 30px)',
                margin: '0 auto',
                position: 'relative',
            }}
            bodyStyle={{
                overflowY: "auto",
                fontFamily: "inter, sans-serif",
            }}
            styles={{
                header: {
                    padding: "12px",
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#f1f5ffff",
                },
                content: {
                    padding: 0,
                    overflow: "hidden",
                },
            }}
            title={
                <div
                    style={{
                        color: "#4b5563",
                        fontFamily: "inter, sans-serif",
                        fontSize: "20px",
                        fontWeight: 500,
                    }}
                >
                    Edit Resignation
                </div>
            }
        >
            <Form
                onFinish={handleSubmit}
                form={form}
                layout="horizontal"
                labelCol={{ span: 5 }}
                wrapperCol={{ span: 19 }}
                colon={false}
                style={{ margin: 0 }}
            >
                <div className="elements">

                    <Form.Item
                        label={<span style={{
                            color: '#4b5563',
                            fontFamily: 'inter, sans-serif',
                            fontSize: '14px',
                            paddingLeft: "15px",
                            fontWeight: 500,
                        }}>Developer <span className="text-danger">*</span></span>}
                        name="developerId"
                        required={false}
                        rules={[{ required: true, message: 'Please select the developer' }]}
                    >
                        <Select placeholder="Select Developer" className="CustomSelect">
                            {users.map(user => (
                                <Select.Option key={user._id} value={user._id}>{user.username}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Applied Date <span className="text-danger">*</span>
                            </span>
                        }
                        name="date"
                        required={false}
                        rules={[{ required: true, message: "Please select date" }]}
                    >
                        <DatePicker
                            className="Allinput"
                            format="DD-MM-YYYY"
                            style={{ width: "100%" }}
                            defaultPickerValue={dayjs()}
                            disabledDate={current => current && current.isAfter(dayjs().endOf("day"))}
                        />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Approved Date <span className="text-danger"></span>
                            </span>
                        }
                        name="approvedDate"
                        required={false}
                        rules={[{ required: false, message: "Please select approvedDate" }]}
                    >
                        <DatePicker className="Allinput" format="DD-MM-YYYY" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                        label={
                            <span style={{
                                color: '#4b5563',
                                fontFamily: 'inter, sans-serif',
                                fontSize: '14px',
                                paddingLeft: "15px",
                                fontWeight: 500,
                            }}>
                                Description <span className="text-red-500">*</span>
                            </span>
                        }
                        name="description"
                        required={false}
                        rules={[{ required: true, message: 'Enter Resignation description' }]}
                    >
                        <Input.TextArea
                            placeholder="Enter Resignation description"
                            className="Allinputtextarea"
                        />
                    </Form.Item>
                    {isAdmins && <Col span={24}>
                        <Form.Item
                            label={
                                <span
                                    style={{
                                        color: "#4b5563",
                                        fontFamily: "inter, sans-serif",
                                        fontSize: "14px",
                                        paddingLeft: "15px",
                                        fontWeight: 500,
                                    }}
                                >
                                    Attachments
                                </span>
                            }
                            name="attachments"
                            valuePropName="fileList"
                            getValueFromEvent={(e) => e.fileList}
                        >
                            <div className="flex items-center gap-4 flex-wrap">
                                <Upload
                                    listType="picture-card"
                                    showUploadList={false}
                                    multiple
                                    fileList={fileListDocument}   // ✅ controlled by state
                                    onChange={(info: UploadChangeParam<UploadFile<RcFile>>) => {

                                        const rawFiles = info.fileList
                                            .map((f) => f.originFileObj)
                                            .filter((f): f is RcFile => !!f);

                                        setDocument(rawFiles);
                                        setFileListDocument(info.fileList);
                                    }}
                                    beforeUpload={() => false}
                                >
                                    <Button icon={<UploadOutlined />}>Upload</Button>
                                </Upload>

                                {/* Show uploaded file preview if exists */}
                                {Document.map((doc, index) => {
                                    if (!doc) return null;

                                    const fileName =
                                        typeof doc === "string"
                                            ? doc.split("/").pop() || ""
                                            : (doc as File).name;

                                    const fileUrl =
                                        typeof doc === "string"
                                            ? `${IMAGEURL}${doc}`
                                            : URL.createObjectURL(doc as File);

                                    return (
                                        <div
                                            key={index}
                                            className="w-38 h-32 border-2 border-gray-200 rounded-lg bg-white relative hover:shadow-md transition-all flex flex-col items-center justify-center p-4"
                                        >
                                            {getFileIcon(fileName)}

                                            <span className="text-xs font-medium text-gray-700 truncate w-full text-center">
                                                {fileName}
                                            </span>

                                            <a
                                                href={fileUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="absolute inset-0"
                                                onClick={(e) => e.stopPropagation()}
                                            />

                                            <button
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleDeleteDocument(doc, index);
                                                }}
                                                className="absolute top-2 right-2 text-red-500 hover:text-red-700 cursor-pointer bg-white rounded-full p-1 flex items-center justify-center"
                                                style={{ width: "22px", height: "22px" }}
                                            >
                                                <DeleteOutlined style={{ fontSize: "12px" }} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        </Form.Item>

                    </Col>}
                </div>

                <Row justify="end">
                    <Col
                        xs={24} sm={24} md={24} xl={24} xxl={20} className="d-flex justify-content-end me-3">
                        <div className="button-wrapper">
                            <button
                                onClick={onCancel}
                                className="allcanclebutton"
                                type="button"
                            >
                                Cancel
                            </button>
                            <button
                                className="allsubmitebutton w-52"
                                type="submit"
                            >
                                Update
                            </button>
                        </div>
                    </Col>
                </Row>
            </Form>
        </Modal>
    )
}

export default EditResignationModal;
